import * as S from "./styles";
import { t } from "@local-logic/core/localization";
import { TrackingEvents, ProductNames } from "@local-logic/core/tracking";
import { Popover, Text } from "@local-logic/core/ui";
import { LatClientBaseUrl } from "@local-logic/lat-client";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useMemo, useState } from "react";
import ArrowSVG from "~/assets/icons/arrow_outward.svg";
import DirectionsIcon from "~/assets/icons/directions.svg";
import { useStateManagerContext } from "~/context/stateManager";
import { useTracking } from "~/context/trackingProvider";
import useIsMobile from "~/hooks/useIsMobile";
import { useLocalContent } from "~/sdks/LocalContent/context";
import { LocalContentProps } from "~/sdks/LocalContent/types";

const CommuteContainer = () => {
  const {
    setCommuteGeojson,
    setDestination,
    showCommuteCalculator,
    setShowCommuteCalculator,
    setIsInputAutoFocused,
  } = useLocalContent();
  const [showTooltip, setShowTooltip] = useState(false);
  const isMobile = useIsMobile();
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const flags = useFlags();
  const tracking = useTracking();

  const openLAT = t("Get neighbourhood report", {
    _key: "openLAT",
    _context: "The text of the CTA that appears for the LAT report",
    _tags: "local_content_sdk",
    _charlimit: 20,
  });

  const openLATTooltip = t(
    "Download your neighbourhood report for this property, which includes a profile of the neighborhood including demographics, climate information, and critical location insights.",
    {
      _key: "openLATTooltip",
      _context: "The text that appears on hover of the CTA for the LAT report",
      _tags: "local_content_sdk",
    }
  );

  const calculateCommute = t("Calculate commute", {
    _key: "calculateCommute",
    _context: "The text that appears on the CTA of the commute calculator",
    _tags: "local_content_sdk",
    _charlimit: 20,
  });

  useEffect(() => {
    if (showCommuteCalculator) {
      tracking.postEvent({
        eventType: TrackingEvents.LOCALCONTENT_COMMUTE_CALC_CLICKED,
        product: ProductNames.LOCAL_CONTENT,
        isMobile,
      });
    }
  }, [showCommuteCalculator]);

  const closeCommute = () => {
    setDestination(undefined);
    setCommuteGeojson(undefined);
    setShowCommuteCalculator(false);
  };

  const openCommute = () => {
    setShowCommuteCalculator(true);
    setIsInputAutoFocused(true);
  };

  const addressLabelParam = useMemo(() => {
    if (sdkState.options?.marker?.addressLabel) {
      return `addressfield=${encodeURIComponent(
        sdkState.options?.marker?.addressLabel
      )}&`;
    }

    return "";
  }, [sdkState.options?.marker?.addressLabel]);

  const openLATUrl = useMemo(() => {
    if (
      sdkState.options?.marker?.addressLabel &&
      sdkState.options?.marker?.lat &&
      sdkState.options?.marker?.lng &&
      sdkState.apiKey
    ) {
      return `${LatClientBaseUrl}/pdf-download/${
        sdkState.apiKey
      }?${new URLSearchParams({
        address_label: sdkState.options?.marker?.addressLabel,
        lat: `${sdkState.options?.marker?.lat}`,
        lng: `${sdkState.options?.marker?.lng}`,
      })}`;
    }

    return "";
  }, [
    sdkState.apiKey,
    sdkState.options?.marker?.addressLabel,
    sdkState.options?.marker?.lat,
    sdkState.options?.marker?.lng,
  ]);

  if (isMobile) {
    return (
      <>
        {showCommuteCalculator ? (
          <S.MobileCommuteContainer>
            <S.CommuteCalculatorMobile onClose={closeCommute} />
          </S.MobileCommuteContainer>
        ) : (
          <S.MobileCommuteToggle
            size="large"
            SVGElement={DirectionsIcon as unknown as React.FC}
            onClick={openCommute}
          />
        )}
      </>
    );
  }

  return (
    <S.DesktopCommuteContainer>
      {showCommuteCalculator ? (
        <S.CommuteCalculator onClose={closeCommute} />
      ) : (
        <S.ButtonContainer>
          {flags.lcLatCta && openLATUrl && (
            <Popover.Root open={showTooltip} onOpenChange={setShowTooltip}>
              <Popover.Trigger asChild>
                <a
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    tracking.postEvent({
                      eventType: TrackingEvents.LOCALCONTENT_LAT_CLICKED,
                      product: ProductNames.LOCAL_CONTENT,
                      isMobile,
                    })
                  }
                  onMouseOver={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  href={openLATUrl}
                >
                  <S.DownloadLATButton
                    variant="outlined"
                    startAdornment={(<ArrowSVG />) as unknown as JSX.Element}
                  >
                    {openLAT}
                  </S.DownloadLATButton>
                </a>
              </Popover.Trigger>
              <S.PopoverContent>
                <Text noMargin color="--ll-color-on-surface-variant1">
                  {openLATTooltip}
                </Text>
              </S.PopoverContent>
            </Popover.Root>
          )}
          <S.DesktopButton
            onClick={openCommute}
            startAdornment={(<DirectionsIcon />) as unknown as JSX.Element}
          >
            {calculateCommute}
          </S.DesktopButton>
        </S.ButtonContainer>
      )}
    </S.DesktopCommuteContainer>
  );
};

export default CommuteContainer;
